import {ApolloClient, ApolloLink, InMemoryCache, createHttpLink, from} from '@apollo/client'
import {setContext} from '@apollo/client/link/context'
import {onError} from '@apollo/client/link/error'
import {fromPromise} from '@apollo/client/link/utils'
import {STORAGE_NAMES, BACKEND_URL} from '../helpers/consts'

export const URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8000'
  : BACKEND_URL

const toWsUrl = (url: string) =>
  url
    .replace(/^https:\/\//i, 'wss://')
    .replace(/^http:\/\//i, 'ws://');

export const WS_URL =
  process.env.NODE_ENV === 'development'
    ? 'ws://localhost:8000'
    : toWsUrl(BACKEND_URL);

const httpLink = createHttpLink({
  uri: `${URL}/api`,
})

const authLink = setContext((_, {headers}) => {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem(STORAGE_NAMES.TOKEN)
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  }
})

const REFRESH_MUTATION = `
  mutation ($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      token
      refreshToken
    }
  }
`

const getStoredUser = () => {
  const rawUser = localStorage.getItem(STORAGE_NAMES.USER)
  if (!rawUser) return null
  try {
    return JSON.parse(rawUser) as {refreshToken?: string; token?: string}
  } catch (error) {
    return null
  }
}

let refreshPromise: Promise<string | null> | null = null

const fetchNewToken = async (): Promise<string | null> => {
  const user = getStoredUser()
  const refreshToken = user?.refreshToken
  if (!refreshToken) return null

  const response = await fetch(`${URL}/api`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      query: REFRESH_MUTATION,
      variables: {refreshToken},
    }),
  })

  const result = await response.json()
  const refreshed = result?.data?.refreshToken
  const newToken = refreshed?.token
  if (!newToken) return null

  const updatedUser = {
    ...user,
    token: newToken,
    refreshToken: refreshed?.refreshToken || refreshToken,
  }
  localStorage.setItem(STORAGE_NAMES.TOKEN, newToken)
  localStorage.setItem(STORAGE_NAMES.USER, JSON.stringify(updatedUser))
  return newToken
}

const shouldRefresh = (graphQLErrors: readonly any[] | undefined, networkError: any) => {
  const isAuthGraphQLError = Boolean(
    graphQLErrors?.some((err) =>
      String(err?.message || '').includes('Signature has expired')
    )
  )

  const statusCode = networkError?.statusCode || networkError?.status
  const isAuthNetworkError = statusCode === 401

  return isAuthGraphQLError || isAuthNetworkError
}

const errorLink = onError(({graphQLErrors, networkError, operation, forward}) => {
  if (!shouldRefresh(graphQLErrors, networkError)) return

  if (!refreshPromise) {
    refreshPromise = fetchNewToken().catch(() => null)
  }

  return fromPromise(refreshPromise)
    .flatMap((newToken) => {
      refreshPromise = null
      if (!newToken) {
        localStorage.removeItem(STORAGE_NAMES.TOKEN)
        localStorage.removeItem(STORAGE_NAMES.USER)
        return forward(operation)
      }

      operation.setContext(({headers = {}}) => ({
        headers: {
          ...headers,
          authorization: `Bearer ${newToken}`,
        },
      }))

      return forward(operation)
    })
})

export const client = new ApolloClient({
  link: from([errorLink as ApolloLink, authLink as ApolloLink, httpLink]),
  cache: new InMemoryCache(),
})

export const prependBackendUrl = (path: string | null | undefined): string => {
  if (!path) return ''
  if (path.startsWith('http')) return path // Ya es URL firmada (S3 en producción)

  // Asegurar que la ruta comience con /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`

  // Si la ruta no incluye /media/, agregarlo
  const fullPath = normalizedPath.startsWith('/media/')
    ? normalizedPath
    : `/media${normalizedPath}`

  return `${URL}${fullPath}`
}
